import { Component, h } from 'preact';
import { WithStyles, withStyles } from '../../../../style';
import { ModalProps } from '../../../../modal';
import { AmPaxAmount, AmPaxType, MAX_AM_PAX_AMOUNT } from '../../../../common';
import { Actions } from '../../../../store';
import { AmPaxSelection } from '../AmPaxSelection/AmPaxSelection';
import { AgeSelect } from '../AgeSelect/AgeSelect';
import { Localizer } from 'preact-i18n';
import { range } from '../../paxAmountUtils';
import Icon from '../../../../common/Icon/Icon';
import produce from 'immer';
import { FinText } from '../../../../i18n';

// Create clamp function which clamps val between min and max (inclusive)
const clamp = (min: number, max: number) => (val: number) => Math.max(min, Math.min(max, val));
const calculateAmount = (type: AmPaxType, amount: number) =>
  clamp(MAX_AM_PAX_AMOUNT[type].minPerRoom, MAX_AM_PAX_AMOUNT[type].maxTotal)(amount);

export interface RoomSelectionProps extends ModalProps {
  paxAmount: AmPaxAmount;
  maxRemainingPaxes: { [key in AmPaxType]: number };
  onRemove: (roomIndex: number) => void;
  canRemove: boolean;
  onUpdate: (paxAmount: AmPaxAmount) => void;
}

interface StoreProps {}

type EffectiveProps = RoomSelectionProps & StoreProps & Actions & WithStyles;

@withStyles()
export default class RoomSelection extends Component<RoomSelectionProps> {
  onAgeChange =
    (idx: number) =>
    (age: number): void => {
      const { paxAmount, onUpdate } = this.props;
      onUpdate(
        produce(paxAmount, ({ childrenAges }: AmPaxAmount) => {
          childrenAges[idx] = age;
        })
      );
    };

  removeRoom = (): void => {
    const { onRemove: onRemoveRoom, paxAmount } = this.props;
    onRemoveRoom(paxAmount.roomIndex);
  };

  addPax = (type: AmPaxType): void => {
    const { onUpdate, paxAmount } = this.props;
    onUpdate(
      produce(paxAmount, (paxAmount: AmPaxAmount) => {
        paxAmount[type] = calculateAmount(type, paxAmount[type] + 1);

        if (type === 'children') {
          const disableInfants = paxAmount.adults < paxAmount.children;
          paxAmount.childrenAges.push(disableInfants ? 2 : 0);
        }
      })
    );
  };

  removePax = (type: AmPaxType): void => {
    const { onUpdate, paxAmount } = this.props;
    onUpdate(
      produce(paxAmount, (paxAmount: AmPaxAmount) => {
        paxAmount[type] = calculateAmount(type, paxAmount[type] - 1);

        if (type === 'children') {
          paxAmount.childrenAges.pop();
        }

        if (type === 'adults' && paxAmount.adults < paxAmount.children) {
          paxAmount.childrenAges = paxAmount.childrenAges.map((age, i) => {
            const ageIsForChildWithNoAdult = i > paxAmount.adults - 1;
            const ageIsLessThanTwo = paxAmount.childrenAges[i] < 2;

            return ageIsForChildWithNoAdult && ageIsLessThanTwo ? 2 : age;
          });
        }
      })
    );
  };

  render({ tx, paxAmount, canRemove, maxRemainingPaxes }: EffectiveProps): h.JSX.Element {
    const roomNumber = paxAmount.roomIndex + 1;
    return (
      <div class="border-bottom">
        <div class="ms-large-t flex flex--middle">
          <span class="nordic-blue-900-text font-body-1 bold-type fill">
            <FinText id="aurinkomatkat.paxAmount.room" /> {roomNumber}
          </span>
        </div>
        <AmPaxSelection
          maxRemainingPaxes={maxRemainingPaxes}
          onIncrease={this.addPax}
          onDecrease={this.removePax}
          type="adults"
          classes={tx('border-bottom')}
          paxAmount={paxAmount}
          autofocus
        />
        <AmPaxSelection
          maxRemainingPaxes={maxRemainingPaxes}
          onIncrease={this.addPax}
          onDecrease={this.removePax}
          type="children"
          paxAmount={paxAmount}
        />
        {range(0, paxAmount.children).map((_i, idx) => (
          <AgeSelect
            key={idx}
            childId={idx + 1}
            value={paxAmount.childrenAges[idx]}
            disableInfants={paxAmount.adults < paxAmount.children && idx + 1 > paxAmount.adults}
            onAgeChange={this.onAgeChange(idx)}
          />
        ))}
        {canRemove && (
          <Localizer>
            <button onClick={this.removeRoom} data-test-remove-room={paxAmount.roomIndex}>
              <div class="ms-large-y flex flex--middle nordic-blue-900-text font-body-1">
                <span class="ms-xsmall-r">
                  <Icon iconName="minus-outline" class={tx('is-small')} />
                </span>
                <FinText id="aurinkomatkat.paxAmount.removeRoom" fields={{ roomNumber: String(roomNumber) }} />
              </div>
            </button>
          </Localizer>
        )}
      </div>
    );
  }
}
